import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { CheckCircle, ChevronLeft, ChevronRight, Image as ImageIcon, Star, Quote, User, ChevronDown, Linkedin } from 'lucide-react';

const SkillExperienceCard = ({ 
  achievement, 
  experience, 
  selectedSkills, 
  cvService, 
  onSkillClick,
  showImages = false
}) => {
  if (!achievement || !experience) return null;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showRecommendations, setShowRecommendations] = useState(false);

  // Fonction pour rendre le niveau de maîtrise
  const renderSkillLevel = (level) => {
    if (!level) return null;
    return (
      <div className="flex items-center space-x-1 ml-2">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full ${
              i < level ? 'bg-cyan' : 'bg-space-600'
            }`}
          />
        ))}
      </div>
    );
  };

  // Composant carrousel d'images
  const ImageCarousel = ({ images }) => {
    if (!images || images.length === 0) return null;

    const nextImage = () => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToImage = (index) => {
      setCurrentImageIndex(index);
    };

    return (
      <div className="mb-4">
        <h5 className="text-xs font-semibold mb-2 text-ink-muted uppercase tracking-wide flex items-center">
          <ImageIcon size={12} className="mr-1" />
          {cvService.locale === 'fr' ? 'Illustrations' : 'Illustrations'}
        </h5>
        <div className="relative bg-space-800 rounded-lg overflow-hidden border border-hair">
          {/* Image principale */}
          <div className="relative h-48 overflow-hidden">
            <img
              src={images[currentImageIndex]}
              alt={`Illustration ${currentImageIndex + 1}`}
              className="w-full h-full object-cover transition-all duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback en cas d'erreur de chargement */}
            <div className="absolute inset-0 bg-space-700 flex items-center justify-center text-ink-dim hidden">
              <ImageIcon size={32} />
            </div>
          </div>

          {/* Contrôles de navigation (si plusieurs images) */}
          {images.length > 1 && (
            <>
              {/* Boutons précédent/suivant */}
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-all duration-200"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 transition-all duration-200"
              >
                <ChevronRight size={16} />
              </button>

              {/* Indicateurs de pagination */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToImage(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentImageIndex
                        ? 'bg-space-800 shadow-md'
                        : 'bg-space-900/40 hover:bg-space-700/70'
                    }`}
                  />
                ))}
              </div>

              {/* Compteur d'images */}
              <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                {currentImageIndex + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Composant pour afficher les recommandations dans SkillExperienceCard
  const SkillCardRecommendations = ({ recommendations }) => {
    if (!recommendations || recommendations.length === 0) return null;

    return (
      <div className="mb-4">
        <div 
          className="flex items-center justify-between cursor-pointer bg-gradient-to-br from-amber/10 to-amber/10 p-3 rounded-lg border border-amber/30 hover:border-amber/50 transition-colors"
          onClick={() => setShowRecommendations(!showRecommendations)}
        >
          <div className="flex items-center">
            <Star size={14} className="mr-2 text-amber" />
            <h5 className="text-sm font-semibold text-amber-soft">
              {cvService.locale === 'fr' ? 'Recommandations' : 'Recommendations'}
            </h5>
            <Badge variant="secondary" className="ml-2 bg-amber/15 text-amber-soft text-xs">
              {recommendations.length}
            </Badge>
          </div>
          <ChevronDown 
            size={16} 
            className={`text-amber transition-transform duration-200 ${showRecommendations ? 'rotate-180' : ''}`}
          />
        </div>

        {showRecommendations && (
          <div className="mt-3 space-y-3">
            {recommendations.map((recommendation, index) => (
              <div key={index} className="bg-space-800 p-3 rounded-lg border border-amber/20 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-7 h-7 bg-gradient-to-br from-amber to-amber-soft rounded-full flex items-center justify-center">
                      <User size={12} className="text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <h6 className="text-sm font-semibold text-ink">
                          {recommendation.author}
                        </h6>
                        {recommendation.linkedin && (
                          <a
                            href={recommendation.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan hover:text-cyan-soft transition-colors"
                            title={cvService.locale === 'fr' ? 'Voir le profil LinkedIn' : 'View LinkedIn profile'}
                          >
                            <Linkedin size={12} />
                          </a>
                        )}
                      </div>
                      <span className="text-xs text-ink-dim">
                        {recommendation.date}
                      </span>
                    </div>
                    <div className="relative">
                      <Quote size={10} className="absolute -top-1 -left-1 text-amber" />
                      <p className="text-base text-ink-muted leading-relaxed pl-2 italic">
                        "{recommendation.text}"
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const achTitle = cvService.getTranslatedValue(achievement.title);
  const achDescription = cvService.getTranslatedValue(achievement.description);
  const extendedDescription = cvService.getTranslatedValue(achievement.extendedDescription);
  
  // Contexte minimal
  const organization = cvService.getTranslatedValue(experience.organization);
  const title = cvService.getTranslatedValue(experience.title);
  const dateRange = cvService.formatDateRange(experience.dateRange);
  
  // Mission summary ou fallback sur description
  const missionSummary = cvService.getTranslatedValue(experience.missionSummary) || achDescription;

  // Filtrer les achievements selon les skills sélectionnés
  const achievementLines = extendedDescription ? extendedDescription.split('\n').filter(line => line.trim()) : [];
  
  // Vérifier si cette achievement contient au moins un des skills sélectionnés
  const hasAnySelectedSkill = selectedSkills.some(selectedSkill => 
    achievement.skillsUsed?.some(skill => 
      cvService.getTranslatedValue(skill.id).toLowerCase() === selectedSkill.toLowerCase()
    )
  );

  if (!hasAnySelectedSkill && selectedSkills.length > 0) {
    return null; // Ne pas afficher si ne contient aucun des skills sélectionnés
  }

  return (
    <div className="bg-gradient-to-br from-space-800 to-space-900/50 backdrop-blur-sm border border-hair shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl p-6 mb-4">
      {/* 1. Mission (titre principal) */}
      <h3 className="text-lg font-bold text-ink mb-3 tracking-tight">
        {achTitle}
      </h3>

      {/* 2. Résumé / description courte */}
      {missionSummary && (
        <p className="text-ink-muted text-base leading-relaxed mb-4 bg-space-800/30 p-3 rounded-lg border-l-4 border-hair-bright">
          {missionSummary.length > 150 ? `${missionSummary.substring(0, 150)}...` : missionSummary}
        </p>
      )}

      {/* 3. Achievements filtrés */}
      {achievementLines.length > 0 && (
        <div className="mb-4">
          <ul className="space-y-2">
            {achievementLines.map((line, index) => (
              <li key={index} className="flex items-start text-sm text-ink-muted">
                <span className="text-ink-dim mr-2 mt-1">•</span>
                <span className="leading-relaxed">{line.replace(/^[-•]\s*/, '')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 4. Compétences utilisées */}
      {achievement.skillsUsed && achievement.skillsUsed.length > 0 && (
        <div className="mb-4">
          <h5 className="text-xs font-semibold mb-2 text-ink-muted uppercase tracking-wide">
            {cvService.locale === 'fr' ? 'Compétences' : 'Skills'}
          </h5>
          <div className="flex flex-wrap gap-2">
            {achievement.skillsUsed.map((skill, index) => {
              const skillName = cvService.getTranslatedValue(skill.id);
              const isSelected = selectedSkills.some(selected => 
                selected.toLowerCase() === skillName.toLowerCase()
              );
              
              return (
                <Badge 
                  key={index} 
                  variant={isSelected ? "default" : "secondary"}
                  className={`text-xs px-3 py-1 font-medium cursor-pointer transition-all duration-200 flex items-center ${
                    isSelected 
                      ? 'bg-cyan text-space-950 border-cyan hover:bg-cyan-soft shadow-md' 
                      : 'bg-space-700 text-ink-muted border-hair hover:bg-space-600'
                  }`}
                  onClick={() => onSkillClick && onSkillClick(skillName)}
                >
                  {isSelected && <CheckCircle size={12} className="mr-1" />}
                  <span>{skillName}</span>
                  {skill.level && renderSkillLevel(skill.level)}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. Carrousel d'images (conditionnel) */}
      {showImages && <ImageCarousel images={achievement.images_url} />}

      {/* 6. Recommandations */}
      <SkillCardRecommendations recommendations={experience.recommendations} />

      {/* 7. Contexte minimal (footer) */}
      <div className="pt-3 border-t border-hair">
        <p className="text-xs text-ink-dim font-medium">
          {organization} — {title} — {dateRange}
        </p>
      </div>
    </div>
  );
};

export default SkillExperienceCard;
